import { useState, useRef } from "react";
import axios from "axios";
import { FileText, Upload, CheckCircle, XCircle, AlertTriangle, FileCheck, Download, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AiExplainCard } from "../components/AiExplain";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const CdarValidator = () => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);
  const [fileSizeError, setFileSizeError] = useState(null);
  const [expandedXpath, setExpandedXpath] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.xml')) {
      if (droppedFile.size > MAX_FILE_SIZE) {
        setFileSizeError(`Le fichier (${(droppedFile.size / (1024*1024)).toFixed(2)} Mo) dépasse la limite de 5 Mo.`);
        setFile(null);
        return;
      }
      setFileSizeError(null);
      setFile(droppedFile);
      setResult(null);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > MAX_FILE_SIZE) {
        setFileSizeError(`Le fichier (${(selectedFile.size / (1024*1024)).toFixed(2)} Mo) dépasse la limite de 5 Mo.`);
        setFile(null);
        return;
      }
      setFileSizeError(null);
      setFile(selectedFile);
      setResult(null);
    }
  };

  const resetValidation = () => {
    setFile(null);
    setResult(null);
    setFileSizeError(null);
    setExpandedXpath(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleValidate = async () => {
    if (!file) return;
    setValidating(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/validate/cdar`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      setResult(response.data);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 413) {
        setFileSizeError(detail || "Le fichier dépasse la limite de 5 Mo.");
      } else if (status === 429) {
        setResult({
          valid: false,
          filename: file.name,
          error_message: detail || "Limite de validations atteinte. Revenez demain.",
          issues: [], total_errors: 0, total_warnings: 0, rules_checked: 0,
        });
      } else {
        setResult({
          valid: false,
          filename: file.name,
          error_message: detail || "Erreur lors de la validation",
          issues: [],
          total_errors: 0,
          total_warnings: 0,
          rules_checked: 0,
        });
      }
    } finally {
      setValidating(false);
    }
  };

  const downloadReport = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport-cdar-${new Date().getTime()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card className="shadow-xl border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <Upload className="h-5 w-5 text-teal-600" />
            Importer un fichier CDAR
          </CardTitle>
          <CardDescription className="text-slate-600">
            Cross Domain Acknowledgement and Response — Règles BR-FR-CDV V1.4.0 (norme du 30/06/2026) — Max 5 Mo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            data-testid="cdar-upload-dropzone"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-300 ${
              isDragging
                ? 'border-teal-500 bg-teal-50/50 scale-[1.02]'
                : 'border-slate-300 bg-slate-50/50 hover:border-teal-400 hover:bg-teal-50/30'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="cdar-file-input"
            />
            <FileText className={`h-16 w-16 mx-auto mb-4 transition-colors ${isDragging ? 'text-teal-600' : 'text-slate-400'}`} />
            {file ? (
              <div>
                <p className="text-lg font-semibold text-slate-800 mb-1">{file.name}</p>
                <p className="text-sm text-slate-500">{(file.size / 1024).toFixed(2)} KB</p>
              </div>
            ) : (
              <div>
                <p className="text-lg font-medium text-slate-700 mb-1">Glissez-déposez votre fichier XML CDAR ici</p>
                <p className="text-sm text-slate-500">ou cliquez pour parcourir</p>
              </div>
            )}
          </div>

          {fileSizeError && (
            <Alert variant="destructive" className="mt-4 bg-red-50 border-red-200" data-testid="cdar-file-size-error">
              <AlertDescription className="flex items-center gap-2 text-red-700">
                <XCircle className="h-4 w-4 flex-shrink-0" />
                {fileSizeError}
              </AlertDescription>
            </Alert>
          )}

          {file && (
            <div className="flex gap-3 mt-6">
              <Button
                data-testid="cdar-validate-button"
                onClick={handleValidate}
                disabled={validating}
                className="flex-1 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white font-semibold py-6 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl"
              >
                {validating ? (
                  <span className="flex items-center gap-2">
                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Validation en cours...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <FileCheck className="h-5 w-5" />
                    {result ? 'Revalider le document' : 'Valider le document'}
                  </span>
                )}
              </Button>
              <Button
                data-testid="cdar-clear-button"
                onClick={resetValidation}
                variant="outline"
                className="px-6 py-6 rounded-xl border-slate-300 hover:bg-slate-100"
              >
                Nouveau fichier
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Summary */}
          <Card className="shadow-xl border-slate-200" data-testid="cdar-result-card">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  {result.valid ? (
                    <div className="bg-green-100 p-3 rounded-xl">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                  ) : (
                    <div className="bg-red-100 p-3 rounded-xl">
                      <XCircle className="h-8 w-8 text-red-600" />
                    </div>
                  )}
                  <div>
                    <CardTitle className="text-2xl mb-2 text-slate-800" data-testid="cdar-result-title">
                      {result.valid ? 'Fichier CDAR valide' : 'Fichier CDAR non conforme'}
                    </CardTitle>
                    <CardDescription className="text-base text-slate-600">
                      {result.error_message
                        ? result.error_message
                        : result.valid
                          ? `${result.rules_checked} règles vérifiées — aucune anomalie détectée.`
                          : `${result.total_errors} erreur(s) et ${result.total_warnings} avertissement(s) détecté(s).`}
                    </CardDescription>
                  </div>
                </div>
                {!result.error_message && (
                  <Button
                    data-testid="cdar-download-report-button"
                    onClick={downloadReport}
                    variant="outline"
                    className="gap-2 rounded-lg border-slate-300 hover:bg-slate-100"
                  >
                    <Download className="h-4 w-4" />
                    Rapport
                  </Button>
                )}
              </div>
            </CardHeader>
            {!result.error_message && (
              <CardContent>
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-slate-50 p-4 rounded-xl text-center">
                    <p className="text-xs text-slate-500 mb-1">Règles vérifiées</p>
                    <p className="text-2xl font-bold text-slate-800" data-testid="cdar-rules-checked">{result.rules_checked}</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-xl text-center">
                    <p className="text-xs text-red-500 mb-1">Erreurs</p>
                    <p className="text-2xl font-bold text-red-600" data-testid="cdar-total-errors">{result.total_errors}</p>
                  </div>
                  <div className="bg-amber-50 p-4 rounded-xl text-center">
                    <p className="text-xs text-amber-500 mb-1">Avertissements</p>
                    <p className="text-2xl font-bold text-amber-600" data-testid="cdar-total-warnings">{result.total_warnings}</p>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* AI error analysis (supporters) */}
          {!result.valid && result.validation_id && (
            <AiExplainCard validationId={result.validation_id} />
          )}

          {/* Issues Table */}
          {result.issues && result.issues.length > 0 && (
            <Card className="shadow-xl border-slate-200" data-testid="cdar-issues-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-800">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  Anomalies détectées ({result.issues.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {result.issues.map((issue, index) => (
                      <div
                        key={index}
                        className={`border rounded-xl p-4 ${issue.severity === 'error' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}
                        data-testid={`cdar-issue-${index}`}
                      >
                        <div className="flex items-start gap-3">
                          {issue.severity === 'error' ? (
                            <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              {issue.rule_id && (
                                <Badge variant="outline" className="font-mono text-xs bg-white border-slate-300">
                                  {issue.rule_id}
                                </Badge>
                              )}
                              <Badge
                                variant="outline"
                                className={`text-xs ${issue.severity === 'error' ? 'border-red-300 bg-red-100 text-red-700' : 'border-amber-300 bg-amber-100 text-amber-700'}`}
                              >
                                {issue.severity === 'error' ? 'Erreur' : 'Avertissement'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-800 mb-2">{issue.message}</p>
                            {issue.location && (
                              <div>
                                <button
                                  onClick={() => setExpandedXpath(expandedXpath === index ? null : index)}
                                  className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors"
                                >
                                  <Info className="h-3 w-3" />
                                  {expandedXpath === index ? 'Masquer le XPath' : 'Voir le XPath'}
                                </button>
                                {expandedXpath === index && (
                                  <pre className="mt-2 text-xs bg-white/80 border border-slate-200 rounded-lg p-3 overflow-x-auto text-slate-600 font-mono">
                                    {issue.location}
                                  </pre>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default CdarValidator;
